/**
 * Shipping-event messaging — M4-1 of the shipping-events program.
 *
 * Resolves "what is the shop promising right now" from the shop metafield
 * `lig_shipping_events.calendar`, which snippet.shipping-events-calendar.liquid
 * serializes onto every page. The PDP renders the active phase's `pdpMessage`
 * (components/product/block-renderers.jsx, add_to_cart block) and each flagged
 * cart line renders its `cartMessage` (components/cart/cart-item.js), so the
 * shopper reads one promise from PDP to payment — the checkout extension in the
 * sister repo renders the same fields from the same metafield.
 *
 * Every string of copy comes from the calendar; nothing holiday-specific is
 * hardcoded here (the app repo's D12 rule). No message resolves → render
 * NOTHING, never a fallback event: a wrong promise is worse than a missing one,
 * the same asymmetry priority-item.js documents for the write path.
 *
 * ⚠️ Boundaries are compared HERE, in JS, at render time — never in Liquid. A
 * cached page's Liquid timestamp is its render time, so a Liquid comparison
 * silently reports whichever phase was active when Shopify last rendered the
 * page — on exactly the surfaces that cache best. The calendar's timestamps are
 * absolute `...Z` instants (`times: "absolute"`), so comparing epoch
 * milliseconds is correct from any shopper timezone; the `localStartsAt` /
 * `localEndsAt` pairs are display-only and never compared.
 *
 * This module deliberately reads ONLY the pinned envelope — `events[]` at the
 * root. A calendar published before the container existed (a bare calendar with
 * `phases` at the root) yields no events and no promise: the sister repo's
 * theme-contract.md pins that a shop still carrying that shape needs
 * re-publishing from the app — an operations problem, not a shape the theme
 * should learn. Do not add a dual-shape reader; the checkout Function carries
 * the compatibility path precisely so nothing downstream has to.
 *
 * Alongside the promise, an event may opt into an EVENT-level
 * per-surface non-priority copy — `ineligiblePdpMessage` (multi-line) and
 * `ineligibleCartMessage` (one line), e.g. "Arrives after Halloween" — for the
 * products it does NOT prioritize: the exact inverse gate, same active-event
 * resolution, and
 * absent-means-silence with no fallback. The two surface pickers below own
 * that branching so both directions of "when in doubt, say nothing" stay
 * unit-tested rather than scattered through components.
 *
 * Wire contract: the shipping-events app repo, docs/theme-contract.md →
 * "The calendar's envelope" and "The non-priority message". Theme-side map:
 * AGENTS.md → "Reading the shipping calendar".
 */

import { isPriorityEligible } from './priority-item';

/** The attribute snippet.shipping-events-calendar.liquid stamps on its JSON script tag. */
export const SHIPPING_EVENTS_CALENDAR_ATTRIBUTE = 'data-shipping-events-calendar';

/**
 * Half-open window test: `startsAt <= now < endsAt`. One phase's `endsAt` is
 * the next one's `startsAt`, so an inclusive test on both ends would match two
 * phases at every boundary instant and the tie would go to whichever this file
 * happened to check first. The checkout Function and the event windows use the
 * same convention.
 */
const between = (nowMs, startsAt, endsAt) => {
  const start = Date.parse(startsAt ?? '');
  const end = Date.parse(endsAt ?? '');
  return Number.isFinite(start) && Number.isFinite(end) && nowMs >= start && nowMs < end;
};

/**
 * The events the calendar carries, read through the pinned envelope only.
 * Anything else — a bare pre-container calendar, a malformed value, null —
 * reads as "no events", which downstream renders as nothing.
 * @param {object|null|undefined} calendar — the parsed metafield value
 * @returns {Array<object>}
 */
export const configuredEvents = (calendar) => (Array.isArray(calendar?.events) ? calendar.events : []);

/**
 * The active event's active phase with its copy resolved, or null when nothing
 * is promised. At most one event is ever active (non-overlap is enforced at
 * save in the app), and only an active phase promises anything — an in-window
 * gap with no covering phase promises nothing, exactly as the checkout
 * Function hides its shipping method there.
 *
 * Copy fields are optional and ABSENT when unauthored (never `""`), so the
 * fallback chain is `pdpMessage || description` on the PDP and
 * `cartMessage || description` beside a cart line — a phase authored before
 * the messaging fields existed still says something.
 *
 * @param {object|null|undefined} calendar — the parsed metafield value
 * @param {number} nowMs — epoch milliseconds (pass Date.now(); a parameter so tests pin the clock)
 * @returns {{event: string|null, method: string|null, phaseId: string|null,
 *   splitShipments: boolean, cartMessage: string|null, pdpMessage: string|null}|null}
 */
export const activeShippingPromise = (calendar, nowMs) => {
  // A `disabled: true` event is OFF — the operator turned it off without
  // deleting it. Skipped exactly like an out-of-window event, so it promises
  // nothing on any surface even mid-window; the Function and the checkout
  // extension apply the same skip. `false` or absent means on.
  const active = configuredEvents(calendar).find(
    (event) => !event?.disabled && between(nowMs, event?.startsAt, event?.endsAt),
  );
  if (!active) return null;

  const phase = (active.phases ?? []).find((candidate) => between(nowMs, candidate?.startsAt, candidate?.endsAt));
  if (!phase) return null;

  return {
    event: active.event ?? null,
    method: active.method ?? null,
    phaseId: phase.id ?? null,
    splitShipments: Boolean(phase.splitShipments),
    cartMessage: phase.cartMessage || phase.description || null,
    pdpMessage: phase.pdpMessage || phase.description || null,
    // Per PHASE (the app moved these off the event on Sep 18 2026 and clears
    // the event-level fields on save), split per surface — the PDP variant is
    // multi-line, the cart variant one line — and deliberately WITHOUT a
    // fallback to the description or any other copy: what to say about an item
    // the event does not prioritize is opt-in, and silence — never the
    // promise — is the correct default for items outside the event. Two
    // back-compat fallbacks so an old published calendar still speaks: the
    // event-level messages (where they lived before the move), then the
    // retired single `ineligibleMessage` that backs BOTH variants (pinned in
    // theme-contract.md — this is contract, not a dual-shape reader).
    ineligiblePdpMessage:
      phase.ineligiblePdpMessage || active.ineligiblePdpMessage || active.ineligibleMessage || null,
    ineligibleCartMessage:
      phase.ineligibleCartMessage || active.ineligibleCartMessage || active.ineligibleMessage || null,
  };
};

/**
 * The copy a PDP shows for its current variant: the promise for an eligible
 * product, the event's opt-in `ineligiblePdpMessage` (multi-line — the render
 * must preserve its newlines) for an ineligible one,
 * nothing for everything in between. Pure so the whole gating matrix is
 * unit-testable; the component just calls it per render.
 *
 * ⚠️ The fail-closed direction INVERTS for the inverse message, which is why
 * `eligibleVariantIds` is checked for array-ness rather than passed straight
 * into `isPriorityEligible`: an ABSENT set means the M1-2 mechanism vanished,
 * and the only safe render is silence — otherwise every PDP on the site,
 * including genuinely eligible products, would claim it misses the event. The
 * layout's `[]` initializer is what makes "wired, nothing eligible" (show the
 * ineligible copy) distinguishable from "mechanism gone" (show nothing), and
 * eligibleVariantIdsFor preserves that distinction: undefined entries stay
 * undefined, while "no product here carries the active key" is `[]`.
 *
 * Non-shippable variants (`requiresShipping === false` — eGift cards,
 * donations) get NO message either way: "arrives after the event" is a false
 * statement about an item that is emailed, and the theme already suppresses
 * its delayed-shipping copy on those lines for the same reason.
 *
 * @param {object|null} promise — activeShippingPromise's result
 * @param {object} surface
 * @param {string|number|null|undefined} surface.variantId — the current variant
 * @param {Array<number|string>|undefined} surface.eligibleVariantIds — the M1-2 set,
 *   readPriorityEligibleVariantIds(window, promise) resolved for THIS promise (M3-5)
 * @param {boolean|undefined} surface.requiresShipping — the current variant's flag
 * @returns {string|null}
 */
export const pdpShippingEventMessage = (promise, { variantId, eligibleVariantIds, requiresShipping }) => {
  if (!promise || requiresShipping === false || !variantId) return null;
  if (!Array.isArray(eligibleVariantIds)) return null;
  return (
    (isPriorityEligible(variantId, eligibleVariantIds) ? promise.pdpMessage : promise.ineligiblePdpMessage) || null
  );
};

/**
 * The copy a cart/mini-cart line shows: the promise for a line whose own
 * `_priorityItem` property claims it, the event's opt-in `ineligibleCartMessage`
 * (one line, per the contract — the cart chip is a compact row)
 * for any other line — explicit `"false"` and absent gate identically, exactly
 * as they mean the same thing downstream. Non-shipping lines
 * (`requiresShipping === false`) show nothing either way; see
 * pdpShippingEventMessage for why.
 *
 * @param {object|null} promise — activeShippingPromise's result
 * @param {object} lineFacts
 * @param {boolean} lineFacts.isPriorityLine — the line's own flag test, computed by the caller
 * @param {boolean|undefined} lineFacts.requiresShipping — the line's flag
 * @returns {string|null}
 */
export const cartLineShippingEventMessage = (promise, { isPriorityLine, requiresShipping }) => {
  if (!promise || requiresShipping === false) return null;
  return (isPriorityLine ? promise.cartMessage : promise.ineligibleCartMessage) || null;
};

/**
 * Read the calendar the layout snippet serialized into the page. Takes `doc`
 * (rather than reading `document` directly) so callers stay unit-testable and
 * the attribute literal lives in exactly one JS file. Every degenerate case —
 * tag missing, empty, `null` (metafield unset), malformed JSON — resolves to
 * null, which every consumer renders as nothing.
 * @param {Document|{querySelector: Function}} doc — document (or a stand-in under test)
 * @returns {object|null} the parsed calendar container
 */
export const readShippingEventsCalendar = (doc) => {
  const raw = doc?.querySelector?.(`script[${SHIPPING_EVENTS_CALENDAR_ATTRIBUTE}]`)?.textContent;
  if (!raw || !raw.trim()) return null;
  try {
    const parsed = JSON.parse(raw);
    return typeof parsed === 'object' ? parsed : null;
  } catch {
    // A malformed calendar must degrade to "no messaging", never to a broken page.
    return null;
  }
};
